import { HOSTED_MODE } from "@/lib/config";
import { authFetch } from "@/lib/session-token";
import type { XaaTokenEndpointAuthMethod } from "@/lib/xaa/types";
import type { NegativeTestDiff } from "@/shared/xaa.js";
import { getOrgScopedIssuerSegment } from "./idp-endpoints";

const XAA_API_BASE = HOSTED_MODE ? "/api/web/xaa" : "/api/mcp/xaa";

export interface AsDiscoveryResult {
  issuer?: string;
  tokenEndpoint?: string;
  grantTypesSupported?: string[];
  jwtBearerSupport: "pass" | "warn" | "fail";
  jwtBearerDetail: string;
  hasTokenEndpoint: boolean;
  issuerMismatch: {
    requested: string;
    advertised: string;
    schemeOnly: boolean;
  } | null;
  metadataUrl: string;
}

/**
 * Probe an authorization server's metadata via the inspector server's
 * discovery endpoint (which validates outbound URLs and tries both
 * well-known forms). Throws with the server's message on failure.
 */
export async function discoverAuthorizationServer(input: {
  issuer?: string;
  tokenEndpoint?: string;
}): Promise<AsDiscoveryResult> {
  const response = await authFetch(`${XAA_API_BASE}/discover-as`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });

  const body = (await response.json().catch(() => null)) as
    | (AsDiscoveryResult & { message?: string })
    | null;

  if (!response.ok || !body) {
    throw new Error(body?.message || `Discovery failed (${response.status})`);
  }

  return body;
}

export interface HealthCheckResult {
  ok: boolean;
  status?: number;
  statusText?: string;
  durationMs: number;
  reason?: "timeout" | "unreachable" | "redirect_not_followed";
}

/**
 * Probe a registered health-check URL via the inspector server (which
 * validates the outbound URL). Throws with the server's message when the URL
 * itself is rejected; an unreachable or timed-out target resolves with
 * `ok: false` instead.
 */
export async function checkResourceHealth(
  url: string
): Promise<HealthCheckResult> {
  const response = await authFetch(`${XAA_API_BASE}/health-check`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ url }),
  });

  const body = (await response.json().catch(() => null)) as
    | (HealthCheckResult & { message?: string })
    | null;

  if (!response.ok || !body) {
    throw new Error(
      body?.message || `Health check failed (${response.status})`
    );
  }

  return body;
}

export interface NegativeTestCase {
  mode: string;
  label: string;
  expectedFailure: string;
  outcome: "rejected" | "accepted" | "timeout" | "error";
  verdict: "pass" | "fail" | "policy" | "unknown";
  status?: number;
  detail?: string;
  diff?: NegativeTestDiff;
}

export interface NegativeTestsResult {
  results: NegativeTestCase[];
  failures: number;
}

export interface NegativeTestsInput {
  audience: string;
  resource: string;
  subject?: string;
  // The subject's email — required alongside `subject` on managed runs: the
  // issuer's evaluator does an exact claims-match on BOTH (IDs alone are
  // never trusted), so a managed scorecard without it is denied
  // `identity_claims_mismatch`.
  email?: string;
  clientId?: string;
  scope?: string;
  tokenEndpoint?: string;
  clientSecret?: string;
  tokenEndpointAuthMethod?: XaaTokenEndpointAuthMethod;
  registrationId?: string;
  // Server-target runs: the server resolves the stored secret and discovers
  // the token endpoint from the server's own config.
  serverId?: string;
  projectId?: string;
  // Hosted runs mint the broken assertions under the org-scoped issuer so
  // they carry the same `iss` the positive run used.
  organizationId?: string | null;
  // LOCAL runs only: "hosted" asks the local server to forward the whole run
  // to the hosted issuer, so the broken assertions carry the hosted `iss` the
  // AS actually trusts (a local `iss` would make every case "pass" on issuer
  // mismatch alone).
  issuerMode?: "local" | "hosted";
  // Scoped issuer flavor: "org" (/o/, signed-in members) or "anonymous"
  // (/g/, guest sessions). Defaults to "org".
  issuerKind?: "org" | "anonymous";
}

/**
 * Fire every deliberately-broken ID-JAG mode at the configured authorization
 * server and report, per case, whether it correctly rejected the assertion.
 * Registration-backed runs send only the registration id; the server resolves
 * the stored secret and endpoint.
 */
export async function runNegativeTests(
  input: NegativeTestsInput
): Promise<NegativeTestsResult> {
  const { organizationId, issuerMode, issuerKind, ...requestBody } = input;
  const resolvedIssuerKind = issuerKind ?? "org";
  // Hosted builds hit the scoped PATH; local hosted-issuer runs carry the
  // opt-in in the BODY and the local server forwards server-to-server.
  const scopedSegment = getOrgScopedIssuerSegment(
    organizationId,
    resolvedIssuerKind
  );
  const forwardExtras =
    !HOSTED_MODE && issuerMode === "hosted"
      ? {
          issuerMode,
          ...(organizationId ? { organizationId } : {}),
          ...(resolvedIssuerKind === "anonymous"
            ? { issuerKind: "anonymous" }
            : {}),
        }
      : {};
  const response = await authFetch(
    `${XAA_API_BASE}${scopedSegment}/negative-tests`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...requestBody, ...forwardExtras }),
    }
  );

  const body = (await response.json().catch(() => null)) as
    | (NegativeTestsResult & { message?: string })
    | null;

  if (!response.ok || !body) {
    throw new Error(
      body?.message || `Negative tests failed (${response.status})`
    );
  }

  return body;
}
